/**
 * Деталь номера — ОТДЕЛЬНЫЙ экран с «Назад» (правило форм tenant-app):
 *   1. Шапка: № / тип / этаж / текущий статус.
 *   2. Проживания этого номера: текущее проживание (гость + до даты) +
 *      история проживаний (выезды — из событий статуса source=checkout).
 *   3. Изменение статуса номера → ОТДЕЛЬНЫЙ под-экран (RoomStatusChangeScreen):
 *      смена статуса (свободен/уборка/обслуживание/не в строю/закрыт);
 *      для «не в строю» (out_of_order) — обязательная причина.
 *   4. История изменения статуса с ИНИЦИАТОРОМ/ИСТОЧНИКОМ: from→to, время,
 *      кто/что инициировал (выезд гостя / заявка на уборку / менеджер+причина).
 *
 * Данные: GET /v1/rooms/board (шапка/текущий гость) +
 * GET /v1/rooms/{id}/status-history (история с инициатором).
 */
import { useMemo, useState } from 'react';
import { Button, Card } from '@hsc/ui';
import type { RoomStatusEvent } from '@hsc/types';
import {
  CLEANLINESS_LABEL,
  ROOM_STATUS_LABEL,
  ROOM_STATUS_TONE,
  formatDate,
} from '../../lib/fleet-format.js';
import { useRoomBoard, useRoomStatusHistory } from './useRooms.js';
import { RoomStatusChangeScreen } from './RoomStatusChangeScreen.js';

export interface RoomDetailProps {
  roomId: string;
  typeName?: string | undefined;
  canManage?: boolean;
  onBack: () => void;
}

/** ДД.ММ.ГГГГ ЧЧ:ММ для таймлайна истории. */
function fmtTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusRu(s: string): string {
  return ROOM_STATUS_LABEL[s as keyof typeof ROOM_STATUS_LABEL] ?? CLEANLINESS_LABEL[s] ?? s ?? '—';
}

export function RoomDetail({ roomId, typeName, canManage = false, onBack }: RoomDetailProps): JSX.Element {
  const [changing, setChanging] = useState(false);
  const boardQ = useRoomBoard();
  const historyQ = useRoomStatusHistory(roomId);

  const room = useMemo(
    () => boardQ.data?.items.find((r) => r.room_id === roomId),
    [boardQ.data, roomId],
  );

  // Смена статуса — ОТДЕЛЬНЫЙ под-экран (правило форм).
  if (changing && room) {
    return (
      <RoomStatusChangeScreen
        roomId={roomId}
        number={room.number}
        currentStatus={room.status}
        onBack={() => setChanging(false)}
        onDone={() => setChanging(false)}
      />
    );
  }

  const events = historyQ.data?.events ?? [];
  const checkouts = events.filter((e) => e.source === 'checkout');

  return (
    <section data-testid="room-detail">
      <div className="stays-head">
        <div>
          <div className="cb-crumbs">Номера / № {room?.number ?? '…'}</div>
          <h1 className="hsc-page__title">
            Номер {room?.number ?? ''}
            {room && (
              <span
                className={`fleet-badge fleet-badge--${ROOM_STATUS_TONE[room.status]}`}
                style={{ marginLeft: 10, verticalAlign: 'middle' }}
                data-testid="detail-status"
              >
                {statusRu(room.status)}
              </span>
            )}
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="detail-back">
          ← Назад
        </Button>
      </div>

      {room && (
        <Card>
          <div className="hsc-stack" style={{ gap: 4 }}>
            <div>
              <span className="ta-muted">Тип:</span> {typeName ?? '—'}
            </div>
            <div>
              <span className="ta-muted">Этаж:</span> {room.floor}
            </div>
            <div>
              <span className="ta-muted">Чистота:</span>{' '}
              {CLEANLINESS_LABEL[room.cleanliness] ?? room.cleanliness}
            </div>
            {room.status === 'out_of_order' && room.out_of_order_reason && (
              <div>
                <span className="ta-muted">Причина (не в строю):</span> {room.out_of_order_reason}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Проживания номера */}
      <h2 className="hsc-page__subtitle" style={{ marginTop: 18 }}>
        Проживания
      </h2>
      <Card>
        {room?.current_guest_name ? (
          <div data-testid="detail-current-stay">
            <strong>Текущее проживание:</strong> {room.current_guest_name}
            {room.current_check_out ? ` · до ${formatDate(room.current_check_out)}` : ''}
          </div>
        ) : (
          <p className="ta-muted" data-testid="detail-no-current-stay">
            Сейчас в номере никто не проживает.
          </p>
        )}
        {checkouts.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="ta-muted" style={{ fontSize: 12, marginBottom: 4 }}>
              Прошлые выезды:
            </div>
            <ul className="hsc-stack" style={{ gap: 2, fontSize: 13 }} data-testid="detail-past-stays">
              {checkouts.map((e, i) => (
                <li key={i}>выезд · {fmtTs(e.created_at)}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Смена статуса */}
      {canManage && (
        <div style={{ marginTop: 14 }}>
          <Button onClick={() => setChanging(true)} data-testid="detail-change-status">
            Изменить статус
          </Button>
        </div>
      )}

      {/* История изменения статуса с инициатором/источником */}
      <h2 className="hsc-page__subtitle" style={{ marginTop: 18 }}>
        История статуса
      </h2>
      {historyQ.isLoading ? (
        <Card>
          <p className="ta-muted">Загрузка истории…</p>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <p className="ta-muted" data-testid="detail-history-empty">
            Изменений статуса пока не было.
          </p>
        </Card>
      ) : (
        <ul className="hsc-stack" style={{ gap: 8 }} data-testid="detail-history">
          {events.map((e, i) => (
            <li key={i}>
              <HistoryRow ev={e} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HistoryRow({ ev }: { ev: RoomStatusEvent }): JSX.Element {
  const initiator =
    ev.source === 'manager' && ev.actor_name
      ? `${ev.source_label} (${ev.actor_name})`
      : ev.source_label;
  return (
    <Card>
      <div className="stays-card__top">
        <span>
          <strong>
            {ev.from_status ? `${statusRu(ev.from_status)} → ` : ''}
            {statusRu(ev.to_status)}
          </strong>
        </span>
        <span
          className={`fleet-badge fleet-badge--${ev.source === 'manager' ? 'busy' : 'muted'}`}
          data-testid={`history-source-${ev.source}`}
        >
          {initiator}
        </span>
      </div>
      <div className="stays-card__dates">
        {fmtTs(ev.created_at)}
        {ev.reason ? ` · ${ev.reason}` : ''}
      </div>
    </Card>
  );
}
